import { provenanceSummaryColor } from "./provenance-indicator.js";
import { calculatorDataText } from "./calculator-data-i18n.js";
import { provenanceSourceBadgeClass } from "./provenance-tone.js";

const TRACE_STATUS_PRESENTATION = Object.freeze({
    effective: Object.freeze({
        badgeClass: null,
        statusClass: "status-success",
        fallbackLabel: "Effective",
    }),
    supporting: Object.freeze({
        badgeClass: "badge-outline",
        badgeLabel: Object.freeze([
            "calculator.data.provenance.supporting",
            "Supporting",
        ]),
        statusClass: "status-success",
        fallbackLabel: "Supporting",
    }),
    overridden: Object.freeze({
        badgeClass: "badge-dash",
        badgeLabel: Object.freeze([
            "calculator.data.status.overridden",
            "Overridden",
        ]),
        statusClass: "status-neutral",
        fallbackLabel: "Overridden",
    }),
    refuted: Object.freeze({
        badgeClass: "badge-error",
        statusClass: "status-error",
        fallbackLabel: "Refuted",
    }),
    inactiveCondition: Object.freeze({
        badgeClass: "badge-warning",
        statusClass: "status-warning",
        fallbackLabel: "Inactive condition",
    }),
});

const FACE_DETAIL_LABELS = Object.freeze([
    ["calculator.data.metadata.comment", "Comment"],
    ["calculator.data.metadata.confidence", "Confidence"],
]);
const OBSERVATION_DETAIL_LABELS = Object.freeze([
    ["calculator.data.metadata.observation_count", "Observation count"],
]);
const SOURCE_DATE_DETAIL_LABELS = Object.freeze([
    ["calculator.data.metadata.claim_date", "Claim date"],
    ["calculator.data.metadata.imported", "Imported"],
]);
const CONDITION_DETAIL_LABELS = Object.freeze([
    ["calculator.data.metadata.conditions", "Conditions"],
]);
function escapeHtml(value) {
    return String(value ?? "").replace(
        /[&<>"']/g,
        (character) => ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            "\"": "&quot;",
            "'": "&#39;",
        })[character],
    );
}

function record(value, path) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new TypeError(`${path} must be an object`);
    }
    return value;
}

function requiredText(value, path) {
    if (typeof value !== "string" || !value.trim()) {
        throw new TypeError(`${path} must be a non-empty string`);
    }
    return value;
}

function normalizeRow(value, path) {
    const row = record(value, path);
    return Object.freeze({
        label: requiredText(row.label, `${path}.label`),
        value: requiredText(row.value, `${path}.value`),
        icon: row.icon === undefined || row.icon === null || row.icon === ""
            ? ""
            : requiredText(row.icon, `${path}.icon`),
        tone: row.tone === undefined || row.tone === null || row.tone === ""
            ? ""
            : requiredText(row.tone, `${path}.tone`),
    });
}

function normalizeCard(value) {
    const card = record(value, "card");
    if (!Array.isArray(card.rows) || card.rows.length === 0) {
        throw new TypeError("card.rows must contain the leading typed value row");
    }
    return Object.freeze({
        summary: requiredText(card.summary, "card.summary"),
        summaryColor: card.summaryColor == null ? "" : provenanceSummaryColor(card.summaryColor),
        badge: requiredText(card.badge, "card.badge"),
        icon: card.icon === undefined || card.icon === null || card.icon === ""
            ? ""
            : requiredText(card.icon, "card.icon"),
        tone: card.tone === undefined || card.tone === null || card.tone === ""
            ? ""
            : requiredText(card.tone, "card.tone"),
        rows: Object.freeze(card.rows.map((row, index) => (
            normalizeRow(row, `card.rows[${index}]`)
        ))),
    });
}

function localizedLabelSet(entries) {
    return new Set(entries.map(([key, fallback]) => (
        calculatorDataText(key, fallback)
    )));
}

function partitionRows(card) {
    const faceLabels = localizedLabelSet(FACE_DETAIL_LABELS);
    const observationLabels = localizedLabelSet(OBSERVATION_DETAIL_LABELS);
    const sourceDateLabels = localizedLabelSet(SOURCE_DATE_DETAIL_LABELS);
    const conditionLabels = localizedLabelSet(CONDITION_DETAIL_LABELS);
    const valueRow = card.rows[0];
    const faceRows = [];
    const observationRows = [];
    const sourceDateRows = [];
    const conditionRows = [];
    const auditRows = [];
    card.rows.slice(1).forEach((row) => {
        if (conditionLabels.has(row.label)) {
            conditionRows.push(row);
        } else if (observationLabels.has(row.label)) {
            observationRows.push(row);
        } else if (sourceDateLabels.has(row.label)) {
            sourceDateRows.push(row);
        } else if (faceLabels.has(row.label)) {
            faceRows.push(row);
        } else {
            auditRows.push(row);
        }
    });
    return Object.freeze({
        valueRow,
        faceRows: Object.freeze(faceRows),
        observationRows: Object.freeze(observationRows),
        sourceDateRows: Object.freeze(sourceDateRows),
        conditionRows: Object.freeze(conditionRows),
        auditRows: Object.freeze(auditRows),
    });
}

function summaryParts(summary, fallbackStatusLabel) {
    const separator = " · ";
    const boundary = summary.lastIndexOf(separator);
    if (boundary <= 0 || boundary + separator.length >= summary.length) {
        return Object.freeze({
            factLabel: summary,
            statusLabel: fallbackStatusLabel,
        });
    }
    return Object.freeze({
        factLabel: summary.slice(0, boundary),
        statusLabel: summary.slice(boundary + separator.length),
    });
}

/**
 * Returns the fact label exactly as the shared card renderer presents it. This
 * also gives compact provenance surfaces one stable lane identity without
 * inventing a second fact-label parser.
 */
export function provenanceFactCardFactLabel(card) {
    return summaryParts(normalizeCard(card).summary, "").factLabel;
}

function rowMarkup(row) {
    return '<div class="grid min-w-0 grid-cols-[minmax(5rem,auto)_minmax(0,1fr)] gap-x-2">'
        + `<dt class="text-base-content/60">${escapeHtml(row.label)}</dt>`
        + `<dd class="min-w-0 whitespace-pre-wrap break-words font-medium">${escapeHtml(row.value)}</dd>`
        + '</div>';
}

function sourceIconMarkup(icon) {
    if (!icon) {
        return "";
    }
    return '<svg class="fishy-icon size-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true">'
        + `<use width="100%" height="100%" href="/img/icons.svg#fishy-${escapeHtml(icon)}"></use>`
        + '</svg>';
}

function sourceBadgeMarkup(label, tone) {
    const escapedLabel = escapeHtml(label);
    const escapedTone = escapeHtml(tone);
    return `<span class="badge badge-soft badge-sm ${provenanceSourceBadgeClass(tone)} min-w-0 max-w-full" data-provenance-fact-source data-provenance-fact-source-tone="${escapedTone}">`
        + `<span class="block min-w-0 whitespace-normal break-words leading-tight" data-provenance-fact-source-label>${escapedLabel}</span>`
        + '</span>';
}

function compactUtcTimestamp(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})T([01]\d|2[0-3]):([0-5]\d):([0-5]\d)(?:\.\d{1,9})?Z$/.exec(value);
    if (!match) {
        throw new TypeError(
            "imported provenance dates must use a canonical UTC timestamp",
        );
    }
    const [, year, month, day, hour, minute, second] = match;
    const numericYear = Number(year);
    const numericMonth = Number(month);
    const numericDay = Number(day);
    const leapYear = numericYear % 4 === 0
        && (numericYear % 100 !== 0 || numericYear % 400 === 0);
    const daysInMonth = [
        0,
        31,
        leapYear ? 29 : 28,
        31,
        30,
        31,
        30,
        31,
        31,
        30,
        31,
        30,
        31,
    ][numericMonth] ?? 0;
    if (numericDay < 1 || numericDay > daysInMonth) {
        throw new TypeError(
            "imported provenance dates must use a valid canonical UTC timestamp",
        );
    }
    return `${year}-${month}-${day} ${hour}:${minute}:${second} UTC`;
}

function sourceDateMarkup(row) {
    const importedLabel = calculatorDataText(
        "calculator.data.metadata.imported",
        "Imported",
    );
    const importedTimestamp = row.label === importedLabel;
    const visibleValue = importedTimestamp
        ? compactUtcTimestamp(row.value)
        : row.value;
    const exactValueAttributes = importedTimestamp
        ? ` tabindex="0" aria-label="${escapeHtml(row.value)}" data-fishy-tooltip="${escapeHtml(row.value)}"`
        : "";
    return `<div class="min-w-0 break-words text-xs text-base-content/70" data-provenance-fact-date${exactValueAttributes}>`
        + escapeHtml(visibleValue)
        + '</div>';
}

function auditMarkup(rows) {
    if (rows.length === 0) {
        return "";
    }
    const auditLabel = calculatorDataText(
        "calculator.data.provenance.audit_details",
        "Audit details",
    );
    return '<details class="collapse collapse-arrow rounded-box border border-base-300 bg-base-200/40" data-provenance-fact-audit>'
        + '<summary class="collapse-title min-h-0 px-3 py-2 text-xs font-medium">'
        + escapeHtml(auditLabel)
        + `<span class="badge badge-ghost badge-xs ms-2">${rows.length}</span>`
        + '</summary>'
        + '<dl class="collapse-content grid gap-1 px-3 pb-3 text-xs">'
        + rows.map(rowMarkup).join("")
        + '</dl></details>';
}

function normalizedValueMarkup(row, statusBadge = "") {
    return '<div class="rounded-box bg-base-200 px-3 py-2" data-provenance-fact-value>'
        + '<div class="flex min-w-0 flex-wrap items-center gap-1.5">'
        + `<span class="text-[0.65rem] font-semibold uppercase tracking-wide text-base-content/75">${escapeHtml(row.label)}</span>`
        + statusBadge
        + '</div>'
        + `<strong class="block whitespace-pre-wrap break-words text-base">${escapeHtml(row.value)}</strong>`
        + '</div>';
}

function conditionMarkup(rows) {
    if (rows.length === 0) {
        return "";
    }
    return '<dl class="grid gap-1.5" data-provenance-fact-conditions>'
        + rows.map((row) => (
            '<div class="rounded-box border border-base-300 bg-base-200/70 px-3 py-2" data-provenance-fact-condition>'
            + `<dt class="text-[0.65rem] font-semibold uppercase tracking-wide text-base-content/75">${escapeHtml(row.label)}</dt>`
            + `<dd class="whitespace-pre-wrap break-words text-sm font-semibold">${escapeHtml(row.value)}</dd>`
            + '</div>'
        )).join("")
        + '</dl>';
}

/**
 * Renders the shared typed-value surface used by provenance candidates and
 * their resolved graph result.
 */
export function provenanceFactValueMarkup(row) {
    return normalizedValueMarkup(normalizeRow(row, "row"));
}

/**
 * Returns the exact candidate rows that are not promoted into the compact card.
 * The first row is structurally the authored typed value and is never an audit
 * row; compact provenance fields are matched against the active localized
 * presentation labels that produced the candidate card.
 */
export function provenanceFactCardAuditRows(card) {
    return partitionRows(normalizeCard(card)).auditRows;
}

function presentationStatus(status, card, effectiveCard) {
    const statusPresentation = TRACE_STATUS_PRESENTATION[status];
    if (!statusPresentation || status === "supporting") {
        throw new TypeError("status must be effective, overridden, refuted, or inactiveCondition");
    }
    if (effectiveCard === null || effectiveCard === undefined || status !== "overridden") {
        return status;
    }
    return card.rows[0].value === effectiveCard.rows[0].value
        ? "supporting"
        : status;
}

/**
 * Classifies an overridden candidate for presentation against the lane's final
 * typed value. Supporting is deliberately derived rather than accepted as an
 * authored resolver status.
 */
export function provenanceFactCardPresentationStatus({
    card,
    status,
    effectiveCard = null,
}) {
    const normalizedCard = normalizeCard(card);
    const normalizedEffectiveCard = effectiveCard === null || effectiveCard === undefined
        ? null
        : normalizeCard(effectiveCard);
    return presentationStatus(status, normalizedCard, normalizedEffectiveCard);
}

/**
 * Renders one existing typed calculator provenance-candidate presentation.
 * `actionsMarkup` is an explicitly trusted integration seam for existing
 * calculator controls; every value owned by the card itself is HTML-escaped.
 * Callout mode reuses the same card surface without interactive disclosures.
 */
export function provenanceFactCardMarkup({
    card,
    status,
    actionsMarkup = null,
    mode = "interactive",
    effectiveCard = null,
}) {
    const normalizedCard = normalizeCard(card);
    const normalizedEffectiveCard = effectiveCard === null || effectiveCard === undefined
        ? null
        : normalizeCard(effectiveCard);
    const presentedStatus = presentationStatus(
        status,
        normalizedCard,
        normalizedEffectiveCard,
    );
    const statusPresentation = TRACE_STATUS_PRESENTATION[presentedStatus];
    if (mode !== "interactive" && mode !== "callout") {
        throw new TypeError("mode must be interactive or callout");
    }
    if (
        actionsMarkup !== undefined
        && actionsMarkup !== null
        && typeof actionsMarkup !== "string"
    ) {
        throw new TypeError("actionsMarkup must be a trusted string or null");
    }
    const normalizedActionsMarkup = typeof actionsMarkup === "string" && actionsMarkup.length
        ? actionsMarkup
        : null;
    if (mode === "callout" && normalizedActionsMarkup !== null) {
        throw new TypeError("callout cards cannot contain actionsMarkup");
    }
    const rows = partitionRows(normalizedCard);
    const summary = summaryParts(
        normalizedCard.summary,
        statusPresentation.fallbackLabel,
    );
    const compactDetails = rows.faceRows.length
        ? '<dl class="grid gap-1 text-xs" data-provenance-fact-key-details>'
            + rows.faceRows.map(rowMarkup).join("")
            + '</dl>'
        : "";
    const observationDetails = rows.observationRows.length
        ? '<dl class="grid gap-1 text-xs" data-provenance-fact-observation-details>'
            + rows.observationRows.map(rowMarkup).join("")
            + '</dl>'
        : "";
    const actions = normalizedActionsMarkup === null
        ? ""
        : '<details class="collapse collapse-arrow rounded-box border border-base-300 bg-base-200/40" data-provenance-fact-actions>'
            + `<summary class="collapse-title min-h-0 px-3 py-2 text-xs font-medium">${escapeHtml(calculatorDataText(
                "calculator.data.provenance.actions",
                "Actions",
            ))}</summary>`
            + `<div class="collapse-content card-actions grid gap-2 px-3 pb-3">${normalizedActionsMarkup}</div>`
            + '</details>';

    const statusLabel = statusPresentation.badgeLabel
        ? calculatorDataText(...statusPresentation.badgeLabel)
        : summary.statusLabel;
    const statusBadge = statusPresentation.badgeClass === null
        ? ""
        : `<span class="badge badge-soft badge-sm ${statusPresentation.badgeClass}" data-provenance-fact-status="${escapeHtml(presentedStatus)}">${escapeHtml(statusLabel)}</span>`;
    const cardStyle = presentedStatus === "overridden" ? "card-dash" : "card-border";

    return `<article class="card card-xs ${cardStyle} bg-base-100" data-provenance-fact-card data-provenance-fact-card-mode="${mode}">`
        + '<div class="card-body gap-2 p-3">'
        + '<div class="flex min-w-0 flex-wrap items-center gap-2">'
        + `<span class="status status-sm ${statusPresentation.statusClass}" aria-hidden="true"></span>`
        + `<h4 class="min-w-0 grow text-sm font-semibold"${normalizedCard.summaryColor ? ` style="color:${normalizedCard.summaryColor}"` : ""}>${escapeHtml(summary.factLabel)}</h4>`
        + '</div>'
        + '<div class="flex min-w-0 items-center gap-2 overflow-hidden" data-provenance-fact-source-row>'
        + sourceIconMarkup(normalizedCard.icon)
        + sourceBadgeMarkup(normalizedCard.badge, normalizedCard.tone)
        + '</div>'
        + rows.sourceDateRows.map(sourceDateMarkup).join("")
        + observationDetails
        + normalizedValueMarkup(rows.valueRow, statusBadge)
        + conditionMarkup(rows.conditionRows)
        + compactDetails
        + (mode === "interactive" ? auditMarkup(rows.auditRows) : "")
        + actions
        + '</div></article>';
}
